import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  PawPrint, Syringe, Calendar, Stethoscope, ArrowRight,
  Check, Smartphone, ShieldCheck, Sparkles, Zap,
  BarChart3, Cloud, Lock, Star, Mail, Bell,
  Heart, FileText, TrendingUp, Users, LayoutDashboard, Home, Search, Moon,
} from 'lucide-react';
import { useSeo } from '../utils/useSeo';

/* ─── küçük yardımcı ─── */
const Tag: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'teal' }) => {
  const styles: Record<string, string> = {
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold ${styles[color] || styles.teal}`}>
      {children}
    </span>
  );
};

/* ─── fake hasta listesi ─── */
const PATIENTS = [
  { name: 'Maya', breed: 'Golden Retriever', owner: 'Ayşe K.', status: 'Sağlıklı', statusColor: 'emerald', date: 'Bugün 09:30', avatar: 'M', avatarBg: 'from-amber-400 to-orange-500' },
  { name: 'Pamuk', breed: 'British Shorthair', owner: 'Mehmet Y.', status: 'Kontrol', statusColor: 'blue', date: 'Bugün 10:15', avatar: 'P', avatarBg: 'from-slate-400 to-slate-600' },
  { name: 'Karamel', breed: 'Labrador', owner: 'Zeynep A.', status: 'Aşı Var', statusColor: 'amber', date: 'Bugün 11:00', avatar: 'K', avatarBg: 'from-amber-600 to-yellow-700' },
  { name: 'Boncuk', breed: 'Siamese Kedi', owner: 'Ali D.', status: 'Sağlıklı', statusColor: 'emerald', date: 'Bugün 11:45', avatar: 'B', avatarBg: 'from-purple-400 to-purple-600' },
];

const LandingPage: React.FC = () => {
  useSeo({
    title: 'KlinikVet | Veteriner Kliniği Yönetim Yazılımı ve Hasta Sahibi Uygulaması',
    description: 'Veteriner klinikleri için hasta dosyaları, randevu, muayene ve aşı takibi tek platformda. Hasta sahipleri için iOS ve Android mobil uygulama. Ücretsiz demo talep edin.',
    path: '/',
  });
  const [scrolled, setScrolled] = useState(false);
  const [activePatient, setActivePatient] = useState(0);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  /* otomatik hasta seçimi */
  useEffect(() => {
    const t = setInterval(() => setActivePatient(p => (p + 1) % PATIENTS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-900 overflow-x-hidden antialiased">

      {/* ════════════ NAVBAR ════════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ backgroundColor: '#2F4420' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform" style={{ backgroundColor: '#557236' }}>
              <img src="/brand-symbol.png" alt="" className="h-6 w-auto" />
            </div>
            <span className="font-bold text-[18px] tracking-tight text-white">KlinikVet</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {[
              { href: '#features', label: 'Özellikler' },
              { href: '#how', label: 'Nasıl Çalışır' },
              { href: '#testimonials', label: 'Müşteriler' },
              { href: '#pricing', label: 'Fiyatlandırma' },
            ].map(l => (
              <a key={l.href} href={l.href} className="text-[14px] font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: 'rgba(255,255,255,0.75)' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#fff', e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)', e.currentTarget.style.backgroundColor = 'transparent')}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline-flex items-center text-[14px] font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Giriş Yap
            </Link>
            <Link to="/demo-request" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-[14px] font-bold transition-all shadow-lg"
              style={{ backgroundColor: '#557236' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#3F5728')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#557236')}
            >
              Demo Talep Et
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ════════════ HERO ════════════ */}
      <section className="relative pt-40 pb-8 px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.035]" style={{
            backgroundImage: 'linear-gradient(to right,#000 1px,transparent 1px),linear-gradient(to bottom,#000 1px,transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top,black 20%,transparent 65%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center top,black 20%,transparent 65%)',
          }} />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-gradient-radial from-teal-100/50 via-transparent to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Yeni</span>
            <span className="w-px h-3 bg-slate-200" />
            <span className="text-[12px] font-medium text-slate-700">Mobil aile uygulaması yayında</span>
            <ArrowRight className="w-3 h-3 text-slate-400" />
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mt-7 text-[44px] md:text-[64px] lg:text-[76px] font-bold tracking-[-0.04em] leading-[1.02] text-slate-900">
            Veteriner kliniği<br />
            yönetiminin
            <span className="italic font-serif font-normal text-teal-700"> yeni standardı</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-6 text-[17px] md:text-[19px] text-slate-600 max-w-2xl mx-auto leading-[1.55]">
            Hasta dosyaları, randevular, aşı takibi ve aile uygulaması — modern
            kliniğinizin ihtiyacı olan her şey tek bir platformda.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Link to="/demo-request" className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-[14px] font-semibold transition-all">
              Ücretsiz Demo Talep Et
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link to="/demo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[14px] font-semibold transition-all">
              Demoyu Gez
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12.5px] text-slate-500">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-600" />Kredi kartı gerekmez</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-600" />Hesabınızı biz açarız</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal-600" />Kurulumda yanınızdayız</span>
          </motion.div>
        </div>

        {/* ── APP MOCKUP ── */}
        <motion.div initial={{ opacity: 0, y: 48 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28, duration: 0.7 }}
          className="mt-16 max-w-5xl mx-auto relative">
          {/* Glow */}
          <div className="absolute -inset-x-8 -inset-y-6 bg-gradient-to-b from-teal-100/30 to-transparent rounded-[2.5rem] blur-2xl pointer-events-none" />

          {/* Mac window */}
          <div className="relative rounded-2xl overflow-hidden border border-slate-200/80 shadow-[0_40px_100px_-20px_rgba(47,68,32,0.30)] bg-white">
            {/* Chrome */}
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex gap-1.5 shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-white border border-slate-200 text-[11px] text-slate-500 font-medium">
                  <Lock className="w-3 h-3 text-teal-600" />
                  klinikvet.app/app
                </div>
              </div>
            </div>

            {/* App content */}
            <div className="flex bg-[#F7F4ED] p-3 gap-3" style={{ minHeight: 460 }}>
              {/* Zeytin kenar çubuğu */}
              <div className="w-[190px] shrink-0 rounded-2xl bg-teal-800 flex flex-col overflow-hidden">
                <div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-white/10">
                  <div className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center shrink-0">
                    <PawPrint className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <div className="text-[13px] font-bold text-white leading-tight">KlinikVet</div>
                    <div className="text-[9px] font-medium text-teal-200">Klinik Paneli</div>
                  </div>
                </div>
                <div className="px-3.5 pt-3 pb-1 text-[8px] font-bold uppercase tracking-[0.18em] text-teal-300">Modüller</div>
                <div className="px-2 pb-2 space-y-0.5">
                  {[
                    { icon: Home, label: 'Kontrol Paneli', active: true },
                    { icon: Users, label: 'Hayvan Sahipleri' },
                    { icon: PawPrint, label: 'Hastalar' },
                    { icon: Calendar, label: 'Randevular' },
                    { icon: Stethoscope, label: 'Muayeneler' },
                    { icon: Syringe, label: 'Aşılar' },
                    { icon: FileText, label: 'Hasta Raporu' },
                    { icon: BarChart3, label: 'İstatistikler' },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-xl text-[11px] font-medium cursor-default ${
                          item.active ? 'bg-teal-600/40 text-teal-100' : 'text-teal-200'
                        }`}>
                        <span className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${item.active ? 'bg-teal-600/50' : 'bg-white/10'}`}>
                          <Icon className="w-3 h-3" strokeWidth={2} />
                        </span>
                        {item.label}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-auto px-3 py-3 border-t border-white/10">
                  <div className="rounded-xl px-2 py-1.5 text-center text-[10px] font-medium border border-teal-600/50 bg-teal-600/20 text-teal-100">
                    Destek veya teklif →
                  </div>
                </div>
              </div>

              {/* Main */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white border border-[#E8E2D4] text-[11px] text-slate-600 w-44">
                    <Search className="w-3 h-3 text-slate-500" /> Hızlı ara…
                    <span className="ml-auto text-[9px] font-bold text-slate-500 border border-[#E8E2D4] rounded px-1">⌘K</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-xl border border-[#E8E2D4] flex items-center justify-center text-slate-500"><Moon className="w-3.5 h-3.5" /></div>
                    <div className="text-right">
                      <div className="text-[11px] font-semibold text-[#2B2B2B] leading-tight">Dr. Ayşe Kurt</div>
                      <div className="text-[9px] text-slate-600 leading-tight">Örnek Veteriner Kliniği</div>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-[10px] text-white font-bold">AK</div>
                  </div>
                </div>

                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-teal-600">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
                    <h2 className="text-[17px] font-bold text-[#2B2B2B] leading-tight">İyi günler! 👋</h2>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="px-2.5 py-1.5 rounded-xl bg-white border border-[#E8E2D4] text-[10px] font-semibold text-[#2B2B2B]">Hasta Ekle</div>
                    <div className="px-2.5 py-1.5 rounded-xl bg-teal-600 text-[10px] font-semibold text-white">Yeni Muayene</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2.5 mb-3">
                  <div className="rounded-2xl bg-white border border-[#E8E2D4] p-3">
                    <div className="flex justify-between text-[8px] font-bold uppercase tracking-wider text-slate-600"><span>Aylık İlerleme</span><span className="text-teal-700">73%</span></div>
                    <div className="text-[26px] font-bold leading-none text-[#2B2B2B] mt-2 mb-2">73<span className="text-[14px]">%</span></div>
                    <div className="h-1.5 rounded-full bg-[#F0EBE1] overflow-hidden"><div className="h-full w-[73%] rounded-full bg-gradient-to-r from-teal-600 to-teal-400" /></div>
                  </div>
                  <div className="rounded-2xl bg-white border border-[#E8E2D4] p-3">
                    <div className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Bugünkü Randevular</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <svg viewBox="0 0 64 64" width="52" height="52" aria-hidden="true">
                        <g transform="rotate(-90 32 32)" fill="none" strokeWidth="9">
                          <circle cx="32" cy="32" r="24" stroke="#557236" strokeDasharray="88 63" />
                          <circle cx="32" cy="32" r="24" stroke="#F59E0B" strokeDasharray="50 101" strokeDashoffset="-88" />
                          <circle cx="32" cy="32" r="24" stroke="#F87171" strokeDasharray="12 139" strokeDashoffset="-138" />
                        </g>
                      </svg>
                      <div className="text-[9px] text-slate-700 leading-snug">
                        <div><b>12</b> toplam</div>
                        <div>7 tamamlandı</div>
                        <div>4 bekliyor</div>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white border border-[#E8E2D4] p-3">
                    <div className="flex justify-between text-[8px] font-bold uppercase tracking-wider text-slate-600"><span>Toplam Hastalar</span><span className="text-[13px] text-[#2B2B2B]">124</span></div>
                    <div className="flex items-end gap-1.5 h-[44px] mt-2">
                      {[40, 55, 35, 70, 55, 85].map((h, i) => <div key={i} className="flex-1 rounded-t bg-teal-600" style={{ height: `${h}%` }} />)}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-white border border-[#E8E2D4] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F0EBE1]">
                    <span className="text-[11px] font-bold text-[#2B2B2B]">Yaklaşan Randevular</span>
                    <span className="text-[10px] text-teal-700 font-semibold">Tümünü Gör</span>
                  </div>
                  {PATIENTS.map((p, i) => (
                    <div key={p.name}
                      className={`flex items-center gap-3 px-4 py-2 border-b border-[#F7F4ED] last:border-0 transition-colors ${i === activePatient ? 'bg-teal-50' : ''}`}>
                      <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                        <PawPrint className="w-3 h-3 text-teal-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[11px] font-semibold text-[#2B2B2B]">{p.name}</span>
                        <span className="text-[10px] text-slate-600"> · {p.owner}</span>
                      </div>
                      <Tag color={p.statusColor === 'emerald' ? 'teal' : p.statusColor}>{p.status}</Tag>
                      <div className="text-[10px] text-slate-600 shrink-0">{p.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Floating notification */}
          <motion.div
            initial={{ opacity: 0, x: 20, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="absolute -right-4 top-20 hidden lg:block"
          >
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200 p-3 w-[220px]">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <Syringe className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-900">Aşı Hatırlatması</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Maya'nın kuduz aşısı 3 gün içinde!</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <div className="flex-1 h-1 rounded-full bg-amber-100">
                  <div className="h-1 rounded-full bg-amber-400 w-3/4" />
                </div>
                <span className="text-[9px] text-amber-600 font-bold">3g kaldı</span>
              </div>
            </div>
          </motion.div>

          {/* Floating phone — mobil uygulama */}
          <div className="absolute -bottom-14 -right-2 hidden lg:block">
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
              <div className="relative w-[170px] rounded-[2rem] border-[5px] border-teal-800 shadow-2xl overflow-hidden" style={{ backgroundColor: '#8F9F66' }}>
                <div className="bg-teal-800 h-5 flex items-center justify-center">
                  <div className="w-14 h-1 bg-teal-600 rounded-full" />
                </div>
                <div className="px-2.5 pt-3 pb-14 space-y-2">
                  <div className="rounded-2xl bg-teal-700 p-2.5">
                    <div className="text-[10px] font-bold text-white">Merhaba, Ayşe</div>
                    <div className="text-[8px] text-teal-100">Dostlarınızın sağlığı güvende</div>
                  </div>
                  <div className="rounded-2xl p-2" style={{ backgroundColor: '#FBFAF2' }}>
                    <div className="text-[9px] text-teal-800 font-bold mb-1">Yaklaşan randevular</div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center"><Calendar className="w-2.5 h-2.5 text-teal-700" /></div>
                      <div>
                        <div className="text-[9px] font-semibold text-[#2B2B2B]">Maya · Kontrol</div>
                        <div className="text-[8px] text-slate-600">Yarın 10:00</div>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-2xl p-2" style={{ backgroundColor: '#FBFAF2' }}>
                    <div className="text-[9px] text-teal-800 font-bold mb-1">Yaklaşan aşılar</div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center"><Syringe className="w-2.5 h-2.5 text-amber-700" /></div>
                      <div>
                        <div className="text-[9px] font-semibold text-[#2B2B2B]">Maya · Kuduz</div>
                        <div className="text-[8px] text-slate-600">3 gün kaldı</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Yüzen sekme çubuğu */}
                <div className="absolute left-2 right-2 bottom-2 rounded-full flex justify-around py-1.5" style={{ backgroundColor: '#FBFAF2' }}>
                  {[{ i: Home, l: 'Ana Sayfa', a: true }, { i: Heart, l: 'Hayvanlarım' }, { i: Calendar, l: 'Randevular' }, { i: Users, l: 'Profil' }].map(t => {
                    const I = t.i;
                    return (
                      <div key={t.l} className="flex flex-col items-center">
                        <I className={`w-3 h-3 ${t.a ? 'text-teal-700' : 'text-slate-600'}`} />
                        <span className={`text-[6px] ${t.a ? 'text-teal-800 font-bold' : 'text-slate-600'}`}>{t.l}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ════════════ STATS / TRUST ════════════ */}
      <section className="py-16 px-6 lg:px-8 border-y border-slate-200 bg-white mt-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-10 text-center">
            Türkiye'nin önde gelen klinikleri tarafından tercih ediliyor
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 max-w-4xl mx-auto">
            {[
              { v: '120+', l: 'Aktif Klinik' },
              { v: '25K+', l: 'Kayıtlı Hasta' },
              { v: '80K+', l: 'Aylık İşlem' },
              { v: '%98', l: 'Memnuniyet' },
            ].map((s, i) => (
              <motion.div key={s.l} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="text-center">
                <div className="text-[32px] md:text-[40px] font-bold tracking-tight text-slate-900 leading-none">{s.v}</div>
                <div className="text-[12px] text-slate-500 mt-2 font-medium">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ FEATURES ════════════ */}
      <section id="features" className="py-28 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Özellikler</p>
            <h2 className="text-[36px] md:text-[52px] font-bold tracking-[-0.03em] leading-[1.05] text-slate-900">
              Gerçekten ihtiyacınız olan
              <br />
              <span className="italic font-serif font-normal text-slate-500">tek bir platform.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[16px] leading-relaxed">
              Veterinerler için sıfırdan tasarlandı. Gereksiz karmaşıklık yok, sadece işinizi kolaylaştıran araçlar.
            </p>
          </div>

          {/* Feature rows */}
          <div className="space-y-4">
            {/* Row 1: 3 eşit */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  icon: PawPrint,
                  iconColor: 'text-teal-600',
                  iconBg: 'bg-teal-50',
                  title: 'Hasta Dosyaları',
                  desc: 'Çip no, ırk, yaş, kilo, alerjiler, muayene geçmişi — her şey tek ekranda. Gelişmiş arama ile saniyeler içinde ulaşın.',
                  extra: (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {['Köpek', 'Kedi', 'Kuş', 'Tavşan', '+'].map(t => (
                        <span key={t} className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[11px] font-semibold border border-teal-200">{t}</span>
                      ))}
                    </div>
                  ),
                },
                {
                  icon: Calendar,
                  iconColor: 'text-blue-600',
                  iconBg: 'bg-blue-50',
                  title: 'Akıllı Randevu',
                  desc: 'Takvim ve liste görünümü. Otomatik hatırlatma, çakışma kontrolü, müsaitlik yönetimi.',
                  extra: (
                    <div className="mt-4 space-y-1.5">
                      {['Bugün 09:30 · Maya', 'Bugün 11:00 · Karamel', 'Bugün 14:30 · Pamuk'].map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11px]">
                          <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="text-slate-600">{r}</span>
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  icon: Syringe,
                  iconColor: 'text-purple-600',
                  iconBg: 'bg-purple-50',
                  title: 'Aşı Takibi',
                  desc: 'Resmi aşı kartı, PDF çıktısı, sonraki doz otomatik hatırlatma, eksiksiz dijital arşiv.',
                  extra: (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-slate-600">Aşılanan hastalar</span>
                        <span className="text-[11px] font-bold text-purple-700">%87</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-purple-100 overflow-hidden">
                        <div className="h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 w-[87%]" />
                      </div>
                    </div>
                  ),
                },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                    className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/50 transition-all">
                    <div className={`w-10 h-10 rounded-xl ${f.iconBg} flex items-center justify-center mb-4`}>
                      <Icon className={`w-5 h-5 ${f.iconColor}`} strokeWidth={2} />
                    </div>
                    <h3 className="text-[17px] font-bold text-slate-900 mb-2 tracking-tight">{f.title}</h3>
                    <p className="text-[13px] text-slate-600 leading-relaxed">{f.desc}</p>
                    {f.extra}
                  </motion.div>
                );
              })}
            </div>

            {/* Row 2: 2 büyük */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mobil */}
              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="relative p-6 rounded-2xl bg-teal-800 overflow-hidden hover:shadow-xl transition-all">
                <div className="absolute right-0 top-0 bottom-0 w-48 bg-gradient-to-l from-teal-500/10 to-transparent pointer-events-none" />
                <Smartphone className="w-10 h-10 text-teal-400 mb-4" strokeWidth={1.8} />
                <h3 className="text-[20px] font-bold text-white mb-2 tracking-tight">Aile Uygulaması</h3>
                <p className="text-[13px] text-slate-300 leading-relaxed max-w-sm">
                  Hasta sahipleri kendi telefonundan randevu alır, aşı geçmişini ve muayene notlarını görür. iOS ve Android.
                </p>
                <div className="mt-5 flex gap-2">
                  {['App Store', 'Google Play'].map(s => (
                    <div key={s} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-[11px] text-slate-300 font-medium">
                      {s}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Raporlar */}
              <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.07 }}
                className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/50 transition-all">
                <BarChart3 className="w-10 h-10 text-teal-600 mb-4" strokeWidth={1.8} />
                <h3 className="text-[20px] font-bold text-slate-900 mb-2 tracking-tight">Raporlar & İstatistikler</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  Aylık ciro, en aktif müşteriler, aşı dağılımı — kararlarınızı veriye dayandırın.
                </p>
                <div className="mt-5 flex items-end gap-1 h-14">
                  {[30, 50, 35, 70, 45, 80, 60, 90, 55, 75, 85, 95].map((h, i) => (
                    <div key={i} className={`flex-1 rounded-t transition-all ${i === 11 ? 'bg-teal-500' : 'bg-teal-100'}`} style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="flex justify-between mt-1">
                  {['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'].map(m => (
                    <span key={m} className="text-[8px] text-slate-400 flex-1 text-center">{m}</span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Row 3: 4 küçük */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Stethoscope, c: 'text-rose-600', bg: 'bg-rose-50', title: 'Muayene', desc: 'Belirti, teşhis, tedavi ve reçete — yapılandırılmış format.' },
                { icon: Bell, c: 'text-amber-600', bg: 'bg-amber-50', title: 'Bildirimler', desc: 'Yeni randevu, gecikmiş aşı, kritik uyarılar anında.' },
                { icon: Cloud, c: 'text-sky-600', bg: 'bg-sky-50', title: 'Bulut', desc: 'Her cihazdan eriş, otomatik yedekleme.' },
                { icon: ShieldCheck, c: 'text-emerald-600', bg: 'bg-emerald-50', title: 'KVKK', desc: 'Şifreli veri, RLS koruması, uyum garantisi.' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="p-5 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all">
                    <div className={`w-9 h-9 rounded-xl ${f.bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-4.5 h-4.5 ${f.c}`} strokeWidth={2} />
                    </div>
                    <h4 className="text-[14px] font-bold text-slate-900 mb-1.5 tracking-tight">{f.title}</h4>
                    <p className="text-[12px] text-slate-600 leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ HOW IT WORKS ════════════ */}
      <section id="how" className="py-28 px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Nasıl Çalışır</p>
            <h2 className="text-[36px] md:text-[52px] font-bold tracking-[-0.03em] leading-[1.05] text-slate-900">
              3 adımda başlayın.
              <br />
              <span className="italic font-serif font-normal text-slate-500">Gerisini biz hallederiz.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {[
              { num: '01', title: 'Demo talep et', desc: 'Formu doldurun, ekibimiz sizi arayıp kliniğiniz için hesabı açsın. Siz hiçbir kurulumla uğraşmayın.', icon: Sparkles },
              { num: '02', title: 'Verileri aktar', desc: "Mevcut hasta listenizi Excel'den içe aktarın veya sıfırdan başlayın. İki yol da çalışır.", icon: Cloud },
              { num: '03', title: 'Hemen kullan', desc: 'Hasta ekle, randevu oluştur, aşı kartı yazdır. Hepsi tek tıkla, eğitim gerektirmez.', icon: Zap },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.num} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-teal-800 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.2} />
                    </div>
                    <span className="text-[40px] font-bold tracking-tighter text-slate-200 leading-none">{s.num}</span>
                  </div>
                  <h3 className="text-[20px] font-bold text-slate-900 mb-2 tracking-tight">{s.title}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════ TESTIMONIALS ════════════ */}
      <section id="testimonials" className="py-28 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Müşteri Yorumları</p>
            <h2 className="text-[36px] md:text-[52px] font-bold tracking-[-0.03em] leading-[1.05] text-slate-900">
              Veterinerler ne diyor?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { quote: 'Eskiden defter tutuyorduk, şimdi her şey parmaklarımızın ucunda. Hasta sahipleri uygulamadan randevu alıyor, çok pratik.', name: 'Dr. Mehmet Yılmaz', role: 'Yılmaz Veteriner Kliniği', city: 'İstanbul', grad: 'from-teal-500 to-cyan-600' },
              { quote: 'Aşı kartı PDF çıktısı çok profesyonel görünüyor. Müşterilerimizin uygulamaya bakmadığı zaman yok!', name: 'Vet. Ayşe Demir', role: 'Pati Veteriner Polikliniği', city: 'Ankara', grad: 'from-purple-500 to-pink-600' },
              { quote: '15 yıldır klinik işletiyorum. Bu yazılım iş akışımı tamamen değiştirdi. İstatistikler büyük yardımcı.', name: 'Dr. Hasan Kaya', role: 'Anadolu Vet Hospital', city: 'İzmir', grad: 'from-amber-500 to-orange-600' },
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-7 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-[15px] text-slate-700 leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.grad} flex items-center justify-center text-white font-bold text-[13px]`}>
                    {t.name.split(' ').slice(-2).map(w => w[0]).join('')}
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-slate-900">{t.name}</p>
                    <p className="text-[11.5px] text-slate-500">{t.role} · {t.city}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ PRICING ════════════ */}
      <section id="pricing" className="py-28 px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Fiyatlandırma</p>
          <h2 className="text-[36px] md:text-[52px] font-bold tracking-[-0.03em] leading-[1.05] text-slate-900">
            Kliniğinize özel
            <br />
            <span className="italic font-serif font-normal text-slate-500">teklif hazırlayalım.</span>
          </h2>
          <p className="mt-6 text-[16px] text-slate-500 leading-relaxed max-w-xl mx-auto">
            Her kliniğin ihtiyacı farklıdır. Sizi dinleyip en uygun planı birlikte belirleyelim — demo talep edin, size özel fiyatlandırmayı konuşalım.
          </p>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="mt-12 rounded-2xl bg-teal-800 p-10 text-left flex flex-col md:flex-row md:items-center gap-8"
          >
            <div className="flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Web */}
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">🖥️</span>
                    <div>
                      <p className="text-white font-bold text-[13px]">Web Uygulaması</p>
                      <p className="text-teal-400 text-[11px] font-semibold">Veterinerler için</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {['Hasta & muayene yönetimi', 'Randevu takvimi', 'İstatistik & raporlar', 'PDF aşı kartı'].map(f => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-teal-400 shrink-0" strokeWidth={2.5} />
                        <span className="text-[12px] text-slate-400">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Mobil */}
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">📱</span>
                    <div>
                      <p className="text-white font-bold text-[13px]">Mobil Uygulama</p>
                      <p className="text-teal-400 text-[11px] font-semibold">Hasta sahipleri için</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {['Evcil hayvan takibi', 'Aşı & randevu bilgisi', 'Sağlık geçmişi', 'iOS & Android'].map(f => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-teal-400 shrink-0" strokeWidth={2.5} />
                        <span className="text-[12px] text-slate-400">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 md:min-w-[210px]">
              <p className="text-slate-400 text-[13px] text-center">Fiyat bilgisi için bizimle iletişime geçin</p>
              <Link to="/demo-request"
                className="w-full text-center px-6 py-3 rounded-xl bg-white text-slate-900 font-semibold text-[14px] hover:bg-slate-100 transition-all shadow-lg">
                İletişime Geç
              </Link>
              <a href="mailto:klinikvet.app@gmail.com"
                className="text-[12px] text-slate-500 hover:text-teal-400 transition-colors">
                klinikvet.app@gmail.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════ FINAL CTA ════════════ */}
      <section className="py-28 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-teal-800 p-12 md:p-16 text-center">
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage: 'linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)',
              backgroundSize: '40px 40px',
            }} />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-radial from-teal-500/20 via-transparent to-transparent rounded-full blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm mb-6">
                <Heart className="w-3 h-3 text-pink-300" fill="currentColor" />
                <span className="text-[11px] font-semibold text-slate-200">Ücretsiz demo, kredi kartı gerekmez</span>
              </div>
              <h2 className="text-[36px] md:text-[52px] font-bold tracking-[-0.03em] leading-[1.05] text-white mb-5">
                Bugün başlayın.
                <br />
                <span className="italic font-serif font-normal text-slate-400">Hemen farkı görün.</span>
              </h2>
              <p className="text-[16px] text-slate-300 max-w-xl mx-auto mb-9 leading-relaxed">
                Formu doldurun, sizi arayıp hesabınızı hazırlayalım. Sorularınız için ekibimiz hazır.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
                <Link to="/demo-request" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 text-[14px] font-bold hover:bg-slate-100 transition-all">
                  Ücretsiz Demo İste
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <a href="mailto:klinikvet.app@gmail.com?subject=KlinikVet%20Hakk%C4%B1nda" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white text-[14px] font-bold border border-white/10 transition-all">
                  <Mail className="w-3.5 h-3.5" />
                  E-posta ile Yaz
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ FOOTER ════════════ */}
      <footer className="border-t border-slate-200 bg-white py-14 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-teal-800 flex items-center justify-center">
                  <PawPrint className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-bold text-slate-900 tracking-tight">KlinikVet</span>
              </div>
              <p className="text-[13px] text-slate-500 leading-relaxed max-w-xs">
                Türkiye'nin yeni nesil veteriner klinik yönetim platformu. Modern, hızlı, güvenli.
              </p>
            </div>

            {[
              { title: 'Ürün', links: [{ l: 'Özellikler', h: '#features' }, { l: 'Fiyatlandırma', h: '#pricing' }, { l: 'Demo', h: '/demo', to: true }] },
              { title: 'Şirket', links: [{ l: 'Hakkımızda', h: '#' }, { l: 'İletişim', h: '/demo-request', to: true }, { l: 'Blog', h: '#' }] },
              { title: 'Yasal', links: [{ l: 'Gizlilik', h: '/gizlilik', to: true }, { l: 'Şartlar', h: '#' }, { l: 'KVKK', h: '/kvkk', to: true }] },
            ].map(col => (
              <div key={col.title}>
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-900 mb-3">{col.title}</h4>
                <ul className="space-y-2 text-[13px]">
                  {col.links.map(l => (
                    <li key={l.l}>
                      {(l as any).to
                        ? <Link to={l.h} className="text-slate-500 hover:text-slate-900 transition-colors">{l.l}</Link>
                        : <a href={l.h} className="text-slate-500 hover:text-slate-900 transition-colors">{l.l}</a>
                      }
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-[12px] text-slate-400">© {new Date().getFullYear()} KlinikVet. Tüm hakları saklıdır.</p>
            <p className="text-[12px] text-slate-400">
              MSE Soft tarafından geliştirildi
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
